
import { PropType, computed, defineComponent, onMounted, reactive, ref, toRefs, watch } from 'vue'
import ObjectTree from '@/components/ObjectTree.vue'
import Toggle from '@/components/Toggle.vue'
import { DEFAULT_ORDER_EVENT_CONFIG, SYNCABLE_SERVICES } from '../constants'
import { OrderEventState } from '../interfaces'
import { format } from '@/utils/template'
import { useStore } from 'vuex'
import { DirectrSyncTypes, Hooks } from '@/models/interfaces/catalogue'
import CronEditor from './CronEditor.vue'
import DToggle from '@/components/base/DToggle.vue'
import DButton from '@/components/base/DButton.vue'
import ControllableView from '@/layouts/components/ControllableView.vue'
import { useQuasar } from 'quasar'

const ORDER_EVENT_PLACEHOLDER = {
  created: 'Order is created successfully',
  accepted: 'Order is accepted without issue',
  rejected: 'Order is rejected',
  fulfilled: 'Order is fulfilled',
  cancelled: 'Order is cancelled',
  issue_resolved: 'Issue is resolved',
  issue: 'Order has an issue',
  transit: 'Order is in transit',
  picked: 'Order is picked up',
}

const PRODUCT_EVENT_PLACEHOLDER = {}

const INVENTORY_EVENT_PLACEHOLDER = {}

export default defineComponent({
  name: 'EventConfigEditor',
  components: { ObjectTree, Toggle, CronEditor, DToggle, ControllableView, DButton },
  props: {
    title: {
      type: String,
      default: 'Event Configuration'
    },
    description: {
      type: String,
      default: 'Configuration for events related to orders, products & inventory-levels'
    },
    modelValue: {
      type: Object,
      required: true
    },
    show: {
      type: Boolean,
      default: true
    },
    types: {
      type: Array as PropType<Array<string>>,
      default: () => [DirectrSyncTypes.Order, DirectrSyncTypes.Product, DirectrSyncTypes.Inventory]
    }
  },
  emits: ['update:model-value', 'save'],
  setup (props, { emit }) {
    const $store = useStore()
    const $sys = $store.getters['Services/Sys']
    const $q = useQuasar()

    const activeView = ref()
    const defaultView = ref({ id: 'order' })
    const views = ref([
      { id: 'order' },
      { id: 'product' },
      { id: 'inventory' },
    ])

    const eventConfig = computed({
      get: () => props.modelValue,
      set: (value) => {
        emit('update:model-value', value)
      }
    })

    const orderState = reactive<OrderEventState>({
      id: computed({ get: () => props.modelValue?.id, set: id => emit('update:model-value', { ...(props.modelValue || {}), id }) }),
      events: Hooks[props.types.includes(DirectrSyncTypes.SourceOrder) ? DirectrSyncTypes.SourceOrder : DirectrSyncTypes.Order],
      services: {}
    })

    const productState = reactive<OrderEventState>({
      id: computed({ get: () => props.modelValue?.id, set: id => emit('update:model-value', { ...(props.modelValue || {}), id }) }),
      events: Hooks[DirectrSyncTypes.Product],
      services: {}
    })

    const inventoryState = reactive<OrderEventState>({
      id: computed({ get: () => props.modelValue?.id, set: id => emit('update:model-value', { ...(props.modelValue || {}), id }) }),
      events: Hooks[DirectrSyncTypes.Inventory],
      services: {}
    })

    const pricingServices = ref<{ id: string, name: string, type: string, [key: string]: any }[]>([])

    onMounted(async () => {
      pricingServices.value = (await $sys.get('/service').then(service => service.filter(service => service.group === 'pricing').map(service => ({ label: service.name, value: service.id }))))
    })

    // const config = computed<CatalogueServiceConfig>({
    //   get () {
    //     return {
    //       sync: { [DirectrSyncTypes.Order]: { active: false }, [DirectrSyncTypes.Product]: { active: false }, [DirectrSyncTypes.Inventory]: { active: false } },
    //       ...(props.modelValue || {})
    //     }
    //   },
    //   set (config) {
    //     emit('update:modelValue', { ...(props.modelValue || {}), ...config })
    //   }
    // })

    function save () {
      emit('update:model-value', eventConfig.value)
      emit('save')
    }

    const cronValue = ref('')

    // HOTFIX since order / source_order are the only ones with event hooks atm, we'll just switch between the two here based
    // on if props.types includes one or the other (mutualy exclusive)
    function addEvent (key) {
      const type = props.types.includes(DirectrSyncTypes.SourceOrder) ? DirectrSyncTypes.SourceOrder : DirectrSyncTypes.Order
      const updated = {
        ...props.modelValue,
        sync: {
          ...(props.modelValue.sync || {}),
          [type]: {
            ...(props.modelValue.sync?.[type] || {}),
            hooks: {
              ...(props.modelValue.sync?.[type]?.hooks || {}),
              [key]: (props.modelValue.sync?.[type]?.hooks || {})[key] || { ...DEFAULT_ORDER_EVENT_CONFIG }
            }
          }
        }
      }

      emit('update:model-value', updated)
    }

    const eventSelection = ref([''])

    watch(eventSelection, (newSelection, oldSelection) => {
      const addedEvent = newSelection.find(event => !oldSelection.includes(event))
      const removedEvent = oldSelection.find(event => !newSelection.includes(event))

      if (addedEvent) addEvent(addedEvent)
      if (removedEvent) removeEvent(removedEvent)
    })

    function removeEvent (key) {
      const type = props.types.includes(DirectrSyncTypes.SourceOrder) ? DirectrSyncTypes.SourceOrder : DirectrSyncTypes.Order
      const c = props.modelValue
      if (c.sync?.[type]?.hooks)
        delete c.sync?.[type].hooks[key]
      emit('update:model-value', c)
    }

    const cronDefaults = {
      'Every five minutes': '*/5 * * * *',
      'Every half hour': '*/30 * * * *',
      'Every hour': '0 * * * *',
      'Every day': '0 0 * * *',
      'Every week': '0 0 * * 0',
    }

    const order_type = ref(DirectrSyncTypes.Order)
    const product_type = ref(DirectrSyncTypes.Product)

    onMounted(async () => {
      const services = await $sys.get('/service')
      orderState.services = services.filter(service => SYNCABLE_SERVICES.includes(service.type))

      if (props.types.includes(DirectrSyncTypes.SourceOrder)) order_type.value = DirectrSyncTypes.SourceOrder
      if (props.types.includes(DirectrSyncTypes.StockItem)) product_type.value = DirectrSyncTypes.StockItem

      // emit defaults & fix model if needed
      const orderHooks = props.modelValue?.sync?.[order_type.value]

      const defaulted = {
        id: props.modelValue?.id || props.modelValue?.service,

        // TODO loop over props.types to set these
        sync: {
          [order_type.value]: { hooks: {} },
          [product_type.value]: { hooks: {} },
          [DirectrSyncTypes.Inventory]: { hooks: {} },
          ...(props.modelValue?.sync || {})
        }
      }

      if (!defaulted.sync[order_type.value].hooks) defaulted.sync[order_type.value].hooks = {}
      if (!defaulted.sync[product_type.value].hooks) defaulted.sync[product_type.value].hooks = {}
      if (!defaulted.sync[DirectrSyncTypes.Inventory].hooks) defaulted.sync[DirectrSyncTypes.Inventory].hooks = {}

      // soft migration..
      // if (props.modelValue?.create && !defaulted.sync[order_type.value]?.hooks?.created)
      //   defaulted.sync[order_type.value].hooks.created = props.modelValue.create
      // if (props.modelValue?.accept && !defaulted.sync[order_type.value]?.hooks?.accepted)
      //   defaulted.sync[order_type.value].hooks.accepted = props.modelValue.accept
      // if (props.modelValue?.reject && !defaulted.sync[order_type.value]?.hooks?.rejected)
      //   defaulted.sync[order_type.value].hooks.rejected = props.modelValue.reject
      // if (props.modelValue?.cancel && !defaulted.sync[order_type.value]?.hooks?.cancelled)
      //   defaulted.sync[order_type.value].hooks.cancelled = props.modelValue.cancel
      // if (props.modelValue?.fulfill && !defaulted.sync[order_type.value]?.hooks?.fulfilled)
      //   defaulted.sync[order_type.value].hooks.fulfilled = props.modelValue.fulfill
      // if (props.modelValue?.resolve_issue && !defaulted.sync[order_type.value]?.hooks?.issue_resolved)
      //   defaulted.sync[order_type.value].hooks.issue_resolved = props.modelValue.resolve_issue
      // if (props.modelValue?.shipment_progress && !defaulted.sync[order_type.value]?.hooks?.shipment_progress)
      //   defaulted.sync[order_type.value].hooks.shipment_progress = props.modelValue.shipment_progress

      emit('update:model-value', defaulted)
      eventSelection.value = Object.keys(defaulted.sync[order_type.value].hooks)
    })

    function toggleSync (cronTimer = '*/5 * * * *') {
      if (!Object.values(DirectrSyncTypes).includes(activeView.value)) return

      const value = props.modelValue?.sync?.[activeView.value]?.active
      const type = activeView.value
      const updated = props.modelValue
      if (!updated.sync) updated.sync = { [type]: { active: value, hooks: {}, trigger: { type: 'cron', timer: cronTimer } } }
      else if (!updated.sync[type]) updated.sync[type] = { active: value, hooks: {}, trigger: { type: 'cron', timer: cronTimer } }
      else {
        updated.sync[type].active = value
        updated.sync[type].trigger = { type: 'cron', timer: cronTimer }
      }
      // else ((updated.sync)[type].active = value) && ((updated.sync)[type].trigger = { type: 'cron', timer: cronTimer })
      emit('update:model-value', updated)
      save()
    }

    function updateOrderEventService (eventKey, e) {
      eventConfig.value.sync[order_type as any].hooks[eventKey].service = e.value || e
    }

    return {
      orderState,
      productState,
      inventoryState,
      pricingServices,
      serviceOptions: computed(() => {
        return Object.keys(orderState.services).map(key => ({ label: orderState.services[key].name, value: orderState.services[key].id }))
      }),
      order_type,
      product_type,
      setUseParentSku: e => {
        const updated = props.modelValue
        updated.sync = {
          ...(updated.sync || {}), [product_type.value]: {
            ...(updated.sync?.product || {}),
            use_parent_sku: e
          }
        }
      },
      eventConfig,
      updateOrderEventService,
      addEvent,
      removeEvent,
      format,
      DirectrSyncTypes,
      toggleSync,
      emit,
      eventSelection,
      ORDER_EVENT_PLACEHOLDER,
      PRODUCT_EVENT_PLACEHOLDER,
      INVENTORY_EVENT_PLACEHOLDER,
      cronValue,
      activeView,
      defaultView,
      views,
      save,
      cronDefaults //catalogue.serivce.timer
    }
  }
})
